// src/client/App.tsx
import { Routes, Route } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, ProtectedRoute } from "@client/contexts/auth-context";
import { queryClient } from "@client/lib/queryClient";
import { Toaster } from "@client/components/ui/toaster";
import { TooltipProvider } from "@client/components/ui/tooltip";

// Layout Components
import { Sidebar } from "@client/components/layout/sidebar";
import { Header } from "@client/components/layout/header";

// Pages
import Login from "@client/pages/login";
import Dashboard from "@client/pages/dashboard";
import Users from "@client/pages/users";
import DbAdmin from "@client/pages/dbadmin";
import NotFound from "@client/pages/not-found";
import Settings from "@client/pages/settings";
import Lists from "@client/pages/lists";
import ProfileManagerPage from "@client/pages/profile-manager";
import ProfileDetailPage from "@client/components/profile-manager/detail-view/ProfileDetailPage"
import ToBeDeveloped from "@client/pages/tobedeveloped";
import UserscriptDownload from "@client/pages/userscript-download";
import ConversationsPage from "@client/pages/conversations";

// Page metadata for headers
const pageInfo = {
  dashboard: {
    title: 'Dashboard',
    description: 'Overview of your LinkedIn automation system'
  },
  users: {
    title: 'User Management',
    description: 'Manage system users and permissions'
  },
  dbadmin: {
    title: 'Database Administration',
    description: 'Inspect and manage database tables'
  },
  settings: {
    title: 'Settings',
    description: 'Configure application settings'
  },
  lists: {
    title: 'List Manager',
    description: 'Organize and manage your contact lists'
  },
  profiles: {
    title: 'Profile Manager',
    description: 'Manage LinkedIn profiles with categories and scoring'
  },
  conversations: {
    title: 'Conversations',
    description: 'Track and manage LinkedIn conversations'
  },
  tobedeveloped: {
    title: 'ToBeDeveloped',
    description: 'The requested page could not be found'
  },
  userscriptdownload: {
    title: 'Userscript Installation',
    description: 'Download and install the TM Universal LinkedIn Interface userscript'
  },
};

// Protected page wrapper with layout
const ProtectedPageWrapper = ({ 
  children, 
  pageKey 
}: { 
  children: React.ReactNode; 
  pageKey: keyof typeof pageInfo;
}) => {
  const info = pageInfo[pageKey];
  
  return (
    <ProtectedRoute>
      <div className="flex h-screen bg-slate-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header 
            title={info.title}
            description={info.description}
          />
          <main className="flex-1 overflow-y-auto">
            {children}
          </main>
        </div>
      </div>
    </ProtectedRoute>
  );
};

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Routes>
            {/* Public Route */}
            <Route path="/login" element={<Login />} />
            
            {/* Protected Routes */}
            <Route
              path="/lists/:id"
              element={
                <ProtectedPageWrapper pageKey="lists">
                  <Lists />
                </ProtectedPageWrapper>
              } 
            />
            
            <Route 
              path="/lists" 
              element={
                <ProtectedPageWrapper pageKey="lists">
                  <Lists />
                </ProtectedPageWrapper>
              } 
            />

            <Route 
              path="/profile-manager/:id" 
              element={
                <ProtectedPageWrapper pageKey="profiles">
                  <ProfileDetailPage />
                </ProtectedPageWrapper>
              } 
            />            

            <Route 
              path="/profile-manager" 
              element={
                <ProtectedPageWrapper pageKey="profiles">
                  <ProfileManagerPage />
                </ProtectedPageWrapper>
              } 
            />            
            
            <Route 
              path="/users" 
              element={
                <ProtectedPageWrapper pageKey="users">
                  <Users />
                </ProtectedPageWrapper>
              } 
            />

            <Route 
              path="/admin" 
              element={
                <ProtectedPageWrapper pageKey="dbadmin">
                  <DbAdmin />
                </ProtectedPageWrapper>
              } 
            />

            <Route 
              path="/settings" 
              element={
                <ProtectedPageWrapper pageKey="settings">
                  <Settings />
                </ProtectedPageWrapper>
              } 
            />

            <Route
              path="/userscript"
              element={
                <ProtectedPageWrapper pageKey="userscriptdownload">
                  <UserscriptDownload />
                </ProtectedPageWrapper>
              }
            />

            <Route
              path="/conversations"
              element={
                <ProtectedPageWrapper pageKey="conversations">
                  <ConversationsPage />
                </ProtectedPageWrapper>
              }
            />

            <Route
              path="/tbd"
              element={
                <ProtectedPageWrapper pageKey="tobedeveloped">
                  <ToBeDeveloped />
                </ProtectedPageWrapper>
              }
            />

            {/* Root - Dashboard */}
            <Route 
              path="/" 
              element={
                <ProtectedPageWrapper pageKey="dashboard">
                  <Dashboard />
                </ProtectedPageWrapper>
              } 
            />
            
            {/* Catch-all 404 */}
            <Route 
              path="*" 
              element={
                <ProtectedPageWrapper pageKey="dashboard">
                  <NotFound />
                </ProtectedPageWrapper>
              } 
            />
          </Routes>
        </TooltipProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
}

export default App;